import { Flex } from '@chakra-ui/react'
import { useState } from 'react'
import { PhoneStatus, TrackedParkingLot } from '~/atoms/WebphoneState'
import SpinningCirclesLoader from '~/components/shared/SpinningCirclesLoader'
import { CallControl } from '~/pages/WebPhone/CallControl'
import { CallHeader } from '~/pages/WebPhone/CallHeader'
import { Size } from '~/types/ThemeTypes'

import { ExtensionPreview } from '@mangovoice/types'

import { CallSoundIndicator } from './CallSoundIndicator'
import { CallSoundIndicatorVariants } from './componentStyles/CallSoundIndicator.style'
import { PhoneComponentSizes } from './componentStyles/Constants'

const {
  Page: { padding: PagePadding },
} = PhoneComponentSizes

interface InCallViewProps {
  callerNames: string[]
  phonenumber?: string
  dtmfHistory: string
  callStartTime: Date | null
  isOnHold: boolean
  isOnline: boolean
  callStatus: PhoneStatus
  isMuted: boolean
  onMuteClick: () => void
  onHoldClick: () => void
  onParkCall: (parkExtension: string) => void
  onTransferCall: (transferTarget: string) => void
  pbxExtensions: ExtensionPreview[]
  parkingLots: TrackedParkingLot[]
  onDialButtonClick: (digit: string) => void
  audioInputs: MediaDeviceInfo[]
  selectedInput: string
  onInputDeviceChange: (deviceId: string) => void
  audioOutputs: MediaDeviceInfo[]
  selectedOutput: string
  onOutputDeviceChange: (deviceId: string) => void
  onCallStop: () => void
  isBelowMaxCallLimit: boolean
  isInConference: boolean
  onMergeCalls: () => void
  onSplitConference: () => void
  onNewCall: () => void
}
export const InCallView = ({
  callerNames,
  phonenumber,
  dtmfHistory,
  callStartTime,
  isOnHold,
  isOnline,
  callStatus,
  isMuted,
  onMuteClick: handleMuteClick,
  onHoldClick: handleHoldClick,
  onParkCall: handleParkCall,
  onTransferCall: handleTransferCall,
  pbxExtensions,
  parkingLots,
  onDialButtonClick: handleDialButtonClick,
  audioInputs,
  selectedInput,
  onInputDeviceChange: handleInputDeviceChange,
  audioOutputs,
  selectedOutput,
  onOutputDeviceChange: handleOutputDeviceChange,
  onCallStop: handleCallStop,
  isBelowMaxCallLimit,
  isInConference,
  onMergeCalls: handleMergeCalls,
  onSplitConference: handleSplitConference,
  onNewCall: handleNewCall,
}: InCallViewProps) => {
  const [isControlDrawerOpen, setIsControlDrawerOpen] = useState(false)
  const toggleDrawer = (isActive: boolean, overrideIsOpen?: boolean) => {
    const isOpen = overrideIsOpen ?? !(isActive && isControlDrawerOpen)
    setIsControlDrawerOpen(isOpen)
  }

  const getAnimationVariant = () => {
    if (!isOnline) {
      return CallSoundIndicatorVariants.Reconnecting
    }
    if (isOnHold) {
      return CallSoundIndicatorVariants.Hold
    }
    if (isMuted) {
      return CallSoundIndicatorVariants.Mute
    }
    return CallSoundIndicatorVariants.Active
  }

  const isRinging =
    callStatus === PhoneStatus.RINGING ||
    callStatus === PhoneStatus.CALL_CONNECTING

  return (
    <Flex
      direction="column"
      height="full"
      justifyContent="space-between"
      align="center"
      py={{
        base: PagePadding.sm.px,
        lg: PagePadding.md.px,
        xl: PagePadding.lg.px,
      }}
    >
      <CallHeader
        size={{ base: Size.sm, lg: Size.md, xl: Size.lg }}
        callerNames={callerNames}
        phonenumber={phonenumber}
        dtmfHistory={dtmfHistory}
        callStartTime={callStartTime}
        isOnHold={isOnHold}
        callStatus={callStatus}
      />
      <Flex zIndex="base">
        {isRinging && (
          <Flex boxSize={{ base: '280px', lg: '360px', xl: '440px' }}>
            <SpinningCirclesLoader />
          </Flex>
        )}
        {!isRinging && (
          <CallSoundIndicator
            size={{ base: Size.sm, lg: Size.md, xl: Size.lg }}
            variant={getAnimationVariant()}
          />
        )}
      </Flex>

      <CallControl
        size={{ base: Size.sm, md: Size.lg, lg: Size.lg, xl: Size.xl }}
        isBelowMaxCallLimit={isBelowMaxCallLimit}
        isInConference={isInConference}
        isRinging={callStatus === PhoneStatus.RINGING}
        isMuted={isMuted}
        onMuteClick={handleMuteClick}
        isOnHold={isOnHold}
        onHoldClick={handleHoldClick}
        onParkCall={handleParkCall}
        onTransferCall={handleTransferCall}
        pbxExtensions={pbxExtensions}
        parkingLots={parkingLots}
        onDialButtonClick={handleDialButtonClick}
        audioInputs={audioInputs}
        selectedInput={selectedInput}
        onInputDeviceChange={handleInputDeviceChange}
        audioOutputs={audioOutputs}
        selectedOutput={selectedOutput}
        onOutputDeviceChange={handleOutputDeviceChange}
        onCallStop={handleCallStop}
        isControlDrawerOpen={isControlDrawerOpen}
        toggleDrawer={toggleDrawer}
        onNewCall={handleNewCall}
        onMergeCalls={handleMergeCalls}
        onSplitConference={handleSplitConference}
        callStatus={callStatus}
      />
    </Flex>
  )
}
